import * as React from 'react'
import { useState } from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import 'dayjs/locale/en'
import { CreatorLayout } from './CreatorLayout'
import { ImportsStep } from './steps/ImportsStep/ImportsStep'
import { DriversStep } from './steps/DriverStep.tsx/DriversStep'
import { ActivityStep } from './steps/ActivityStep/ActivityStep'
import { ViolationsStep } from './steps/ViolationsStep/ViolationsStep'
import { DelegationsFirstSubstep } from './steps/DelegationsStep/DelegationsFirstSubstep'

import {
  legacyToStage,
  stageIndexOf,
  stageToLegacy,
  StageId,
  creatorStages,
} from './steps/creatorSteps'
import { useStepsWithStatus } from '@/app/hooks/useCreatorStepsWithStatus'
import { Step } from './Stepper'
import { EvidenceFirstSubstep } from './steps/EvidenceStep/EvidenceFirstSubstep'
import { EvidenceDetailsSubstep } from './steps/EvidenceStep/EvidenceDetailsSubstep'
import { SummarySecondSubstep } from './steps/SummaryStep/SummarySecondSubstep'
import { SummaryFirstSubstep } from './steps/SummaryStep/SummaryFirstSubstep'
import { Driver, SelectedDriver } from '@/app/types/creator'
import { axiosInstance } from '@/lib/axios'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { ReportType } from '@/app/constants/reportType'
import { getReportTypeByNumber } from '@/app/services/printout/reportTypeResolver'
import { OutputType } from '@/app/constants/outputType'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { addTask } from '@/app/actions/task'
import { TaskType } from '@/app/constants/taskType'
import { HOME_ROUTES } from '@/modules/home/routes-typed'
import {
  clearCreatorWizardResume,
  peekCreatorWizardResume,
} from '@/modules/home/creatorWizardResumeStorage'
import { TableProvider } from '../drivers/context/TableContext'
import { useQueryDriversForm } from '@/lib/api/company/drivers/useQueryDriversForm'
import { useCreatorSelectionStore } from './stores/creatorSelectionStore'

type GroupedTimesheetResponseRow = {
  driver?: {
    id?: number | string
    name?: string
    surname?: string
    fullName?: string
  }
  calculations?: boolean[]
  hasContract?: number[]
  summary?: number | null
  expectedNettoAmount?: number | null
  calculatedNetto?: number | null
  polishTripsAmount?: number | null
  additionAmount?: number | null
  calculatedBonus?: number | null
  reward?: number | null
  leavesGrossAmount?: number | null
  sickLeavesGrossAmount?: number | null
  benefitsGrossAmount?: number | null
  leavesNettoAmount?: number | null
  sickLeavesNettoAmount?: number | null
  includeDomesticBusinessTrip?: boolean | number | string | null
  lastCalculationDate?: string | null
}

const createTaskId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

const normalizePrecalculateResults = (payload: unknown) => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (
    payload &&
    typeof payload === 'object' &&
    'data' in payload &&
    Array.isArray((payload as { data: unknown }).data)
  ) {
    return (payload as { data: unknown[] }).data
  }

  return [] as unknown[]
}

const hasBlockingPrecalculateError = (results: unknown[]) => {
  if (!results.length) {
    return false
  }

  return results.some((item) => {
    if (!item || typeof item !== 'object') {
      return false
    }

    const typeValue =
      (item as { type?: unknown; Type?: unknown }).type ??
      (item as { type?: unknown; Type?: unknown }).Type

    if (typeof typeValue === 'number') {
      return typeValue === 2
    }

    if (typeof typeValue === 'string') {
      return typeValue.toLowerCase() === 'error'
    }

    return false
  })
}

function toDateOnly(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed
}

function mapFormDriverToCreatorDriver(api: any): Driver {
  const status: Driver['status'] =
    api?.status === 'unknown' || api?.status === 'inactive'
      ? api.status
      : 'active'
  return {
    id: String(api?.id ?? ''),
    driver: api?.name ?? '',
    surname: api?.surname ?? '',
    workingDays: Number(api?.workingDays ?? 0),
    nonWorkingDays: Number(api?.nonWorkingDays ?? 0),
    lastCardReadDate: toDateOnly(api?.lastCardReadDate),
    addToCalculation: true,
    status,
    hasAllPersonalData:
      api?.hasAllPersonalData ?? api?.hasAllPesonalData ?? undefined,
    hasContractInPeriod:
      api?.hasContractInPeriod ?? api?.hasContract ?? undefined,
  }
}

interface DispatchProps {
  addTask: (id: string, type: TaskType, data: any) => void
}

const CreatorPageComponent: React.FC<DispatchProps> = ({
  addTask: dispatchAddTask,
}) => {
  const { companyId } = useParams<{ companyId: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const monthParam = searchParams.get('month')
  const stepParam = searchParams.get('step')
  const selectedMonthFromDashboard = React.useMemo(() => {
    if (!monthParam) return dayjs()
    const parsedMonth = dayjs(`${monthParam}-01`)
    return parsedMonth.isValid() ? parsedMonth : dayjs()
  }, [monthParam])

  const startTime = selectedMonthFromDashboard
    .startOf('month')
    .format('YYYY-MM-DD')
  const endTime = selectedMonthFromDashboard.endOf('month').format('YYYY-MM-DD')

  const driversQuery = useQueryDriversForm({
    companyId: companyId!,
    startTime,
    endTime,
    page: 1,
    pageSize: 10000,
    status: 'active',
    enabled: !!companyId,
  })
  const drivers = React.useMemo((): Driver[] => {
    const data = driversQuery.data
    if (!data) {
      return []
    }
    const list = Array.isArray(data) ? data : (data.drivers ?? [])
    return list.map(mapFormDriverToCreatorDriver)
  }, [driversQuery.data])

  const storeCompanyId = useCreatorSelectionStore((s) => s.companyId)
  const storeMonth = useCreatorSelectionStore((s) => s.month)
  const storedSelectedDrivers = useCreatorSelectionStore(
    (s) => s.selectedDrivers,
  )
  const setStoredSelectedDrivers = useCreatorSelectionStore(
    (s) => s.setSelectedDrivers,
  )
  const syncSelectionContext = useCreatorSelectionStore((s) => s.syncContext)

  const isStoredContextMatching =
    storeCompanyId === (companyId ?? null) &&
    storeMonth === (monthParam ?? null)
  const selectedDrivers = isStoredContextMatching ? storedSelectedDrivers : []
  const setSelectedDrivers = setStoredSelectedDrivers

  React.useLayoutEffect(() => {
    syncSelectionContext(companyId ?? null, monthParam ?? null)
  }, [companyId, monthParam, syncSelectionContext])

  const [selectedReports, setSelectedReports] = useState<string[]>([])

  const [currentStageId, setCurrentStageId] = React.useState<StageId>(() => {
    if (stepParam && creatorStages.some((s) => s.id === stepParam)) {
      return stepParam as StageId
    }
    return 'imports'
  })
  const [isFullEvidence, setIsFullEvidence] = React.useState(false)
  const [isGeneratingEvidence, setIsGeneratingEvidence] = React.useState(false)
  const [isGeneratingPrintouts, setIsGeneratingPrintouts] =
    React.useState(false)
  const [generateToSeparateFiles, setGenerateToSeparateFiles] =
    React.useState(false)
  const stepsWithStatus = useStepsWithStatus(currentStageId)

  React.useEffect(() => {
    if (searchParams.get('step') === currentStageId) return
    const next = new URLSearchParams(searchParams)
    next.set('step', currentStageId)
    setSearchParams(next, { replace: true })
  }, [currentStageId, searchParams, setSearchParams])

  const legacyCoords = React.useMemo(
    () => stageToLegacy(currentStageId),
    [currentStageId],
  )

  const settlementMonthLabel = React.useMemo(() => {
    const label = selectedMonthFromDashboard.locale('pl').format('MMMM YYYY')
    return label.charAt(0).toUpperCase() + label.slice(1)
  }, [selectedMonthFromDashboard])

  const dashboardHref = React.useMemo(
    () =>
      `${HOME_ROUTES.dashboard.buildPath({ companyId: companyId })}${monthParam ? `?month=${monthParam}` : ''}`,
    [companyId, monthParam],
  )

  React.useLayoutEffect(() => {
    const pathKey = `${location.pathname}${location.search || ''}`
    const fromStorage = peekCreatorWizardResume(pathKey)
    if (fromStorage) {
      setCurrentStageId(legacyToStage(fromStorage.step, fromStorage.subStep))
      if (fromStorage.selectedDrivers?.length) {
        setSelectedDrivers(fromStorage.selectedDrivers)
      }
      queueMicrotask(() => clearCreatorWizardResume(pathKey))
      return
    }
    const resume = (
      location.state as
        | {
            resumeCreatorWizard?: {
              step: number
              subStep: number
              selectedDrivers?: SelectedDriver[]
            }
          }
        | undefined
    )?.resumeCreatorWizard
    if (resume != null && typeof resume.step === 'number') {
      setCurrentStageId(legacyToStage(resume.step, resume.subStep ?? 0))
      if (resume.selectedDrivers?.length) {
        setSelectedDrivers(resume.selectedDrivers)
      }
    }
  }, [location.pathname, location.search, location.state])

  const refetchDrivers = driversQuery.refetch
  const handleDriversImported = React.useCallback(() => {
    void refetchDrivers()
  }, [refetchDrivers])

  const goToNext = () => {
    const idx = stageIndexOf(currentStageId)
    const next = creatorStages[idx + 1]
    if (next) setCurrentStageId(next.id)
  }

  const goToPrev = () => {
    const idx = stageIndexOf(currentStageId)
    if (idx <= 0) return
    let prev = creatorStages[idx - 1]
    if (prev.id === 'evidence.details' && !isFullEvidence) {
      prev = creatorStages[idx - 2] ?? prev
    }
    setCurrentStageId(prev.id)
  }

  const handleStepClick = (id: string | number) => {
    const idStr = String(id)
    const stage = creatorStages.find((s) => s.id === idStr)
    const targetStageId =
      stage?.id ?? creatorStages.find((s) => s.groupId === idStr)?.id
    if (!targetStageId) return
    if (stageIndexOf(targetStageId) <= stageIndexOf(currentStageId)) {
      setCurrentStageId(targetStageId)
    }
  }

  const handleGenerateEvidence = async (fullEvidence: boolean) => {
    if (isGeneratingEvidence) {
      return
    }

    const selectedDriverIds = selectedDrivers
      .map((driver) => Number(driver.driverId))
      .filter((driverId) => !Number.isNaN(driverId))

    if (!companyId || !selectedDriverIds.length) {
      return
    }

    if (fullEvidence) {
      setIsFullEvidence(true)
      setCurrentStageId('evidence.details')
      return
    }

    setIsFullEvidence(false)
    setIsGeneratingEvidence(true)
    try {
      const month = selectedMonthFromDashboard.month() + 1
      const year = selectedMonthFromDashboard.year()
      await runEvidenceCalculation(selectedDriverIds, month, year)
      setCurrentStageId('summary.printouts')
    } catch (error) {
      console.error('Evidence generation failed', error)
    } finally {
      setIsGeneratingEvidence(false)
    }
  }

  const runEvidenceCalculation = async (
    selectedDriverIds: number[],
    month: number,
    year: number,
  ): Promise<GroupedTimesheetResponseRow[] | null> => {
    if (!companyId || !selectedDriverIds.length) {
      return null
    }

    const taskId = createTaskId()

    try {
      await axiosInstance.post(`/api/${companyId}/timesheet/precalculate`, {
        companyId,
        drivers: selectedDriverIds,
        months: [month],
        year,
        outOfDateQuarterExchangeRateAccepted: false,
        id: taskId,
      })
    } catch (error: any) {
      const results = normalizePrecalculateResults(error?.response?.data)
      if (hasBlockingPrecalculateError(results)) {
        console.error('Evidence precalculation blocked generation', results)
        return null
      }
    }

    await axiosInstance.post(`/api/${companyId}/timesheet/calculate`, {
      companyId,
      drivers: selectedDriverIds,
      months: [month],
      year,
      outOfDateQuarterExchangeRateAccepted: false,
      bonusConfigurations: [],
      id: taskId,
    })

    let groupedTimesheetRows: GroupedTimesheetResponseRow[] = []

    for (let attempt = 0; attempt < 8; attempt++) {
      if (attempt > 0) {
        await sleep(1500)
      }

      const resp = await axiosInstance.post(
        `/api/${companyId}/timesheet/groupedTimesheet`,
        // Reads only hasContract/calculations — bonus config would make the server build full timesheet models per poll.
        {
          companyId,
          drivers: selectedDriverIds,
          year,
          month,
          showBonusConfiguration: false,
        },
      )

      groupedTimesheetRows = Array.isArray(resp.data?.data)
        ? resp.data.data
        : Array.isArray(resp.data)
          ? resp.data
          : []

      const monthIndex = month - 1
      const rowsById = new Map(
        groupedTimesheetRows
          .filter((row) => row.driver?.id != null)
          .map((row) => [Number(row.driver?.id), row]),
      )

      const calculationReadyForAll = selectedDriverIds.every((driverId) => {
        const row = rowsById.get(driverId)
        if (!row) return false
        const hasContractInMonth = row.hasContract?.[monthIndex] ?? 0
        return (
          hasContractInMonth === 0 || Boolean(row.calculations?.[monthIndex])
        )
      })

      if (calculationReadyForAll) break
    }

    return groupedTimesheetRows
  }

  const handleGeneratePrintouts = async () => {
    if (isGeneratingPrintouts) {
      return
    }

    if (!companyId) {
      return
    }

    const selectedDriverIds = selectedDrivers
      .map((driver) => Number(driver.driverId))
      .filter((driverId) => !Number.isNaN(driverId))

    const selectedReportNumbers = selectedReports
      .map((reportId) => Number(reportId))
      .filter((reportNumber) => !Number.isNaN(reportNumber) && reportNumber > 0)
    const selectedReportTypes = selectedReportNumbers
      .map((reportNumber) => getReportTypeByNumber(reportNumber))
      .map((reportType) => (reportType === ReportType.NONE ? null : reportType))

    if (!selectedDriverIds.length) {
      return
    }

    if (!selectedReportNumbers.length) {
      goToNext()
      return
    }

    setIsGeneratingPrintouts(true)

    try {
      const startTime = selectedMonthFromDashboard
        .startOf('month')
        .format(SERVICE_TIME_FORMAT)
      const endTime = selectedMonthFromDashboard
        .endOf('month')
        .format(SERVICE_TIME_FORMAT)

      if (selectedReportNumbers.length > 1) {
        const taskId = createTaskId()
        await axiosInstance.post(`print-api/${companyId}/printout/print`, {
          id: taskId,
          drivers: selectedDriverIds,
          vehicles: null,
          reportType: selectedReportNumbers[0],
          reportTypes: selectedReportNumbers,
          startTime,
          endTime,
          outputType: OutputType.Pdf,
          data: {
            separateFiles: generateToSeparateFiles,
          },
        })
        dispatchAddTask(
          taskId,
          TaskType.Printout,
          selectedReportTypes[0] ?? null,
        )
      } else {
        const reportType = selectedReportNumbers[0]
        const reportData = selectedReportTypes[0] ?? null
        const taskId = createTaskId()

        await axiosInstance.post(`print-api/${companyId}/printout/print`, {
          id: taskId,
          drivers: selectedDriverIds,
          vehicles: null,
          reportType,
          startTime,
          endTime,
          outputType: OutputType.Pdf,
          data: {
            separateFiles: generateToSeparateFiles,
          },
        })

        dispatchAddTask(taskId, TaskType.Printout, reportData)
      }

      goToNext()
    } catch (error) {
      console.error('Printout generation failed', error)
    } finally {
      setIsGeneratingPrintouts(false)
    }
  }

  return (
    <CreatorLayout
      steps={stepsWithStatus as Step[]}
      monthLabel={settlementMonthLabel}
      onStepClick={handleStepClick}
    >
      {currentStageId === 'imports' && (
        <ImportsStep
          companyId={companyId}
          drivers={drivers}
          isLoading={driversQuery.isLoading}
          isRefreshing={driversQuery.isFetching && !driversQuery.isLoading}
          periodStart={startTime}
          monthLabel={settlementMonthLabel}
          onImported={handleDriversImported}
          onNext={goToNext}
        />
      )}
      {currentStageId === 'drivers' && (
        <DriversStep
          drivers={drivers}
          isLoading={driversQuery.isLoading}
          selectedDrivers={selectedDrivers}
          setSelectedDrivers={setSelectedDrivers}
          onNext={goToNext}
          onPrev={goToPrev}
          periodStart={startTime}
        />
      )}
      {currentStageId === 'activity' && (
        <ActivityStep
          selectedDrivers={selectedDrivers}
          drivers={drivers}
          year={selectedMonthFromDashboard.year()}
          onNext={goToNext}
          onPrev={goToPrev}
        />
      )}
      {currentStageId === 'violations' && (
        <TableProvider>
          <ViolationsStep
            companyId={companyId}
            drivers={selectedDrivers}
            onNext={goToNext}
            onPrev={goToPrev}
          />
        </TableProvider>
      )}
      {currentStageId === 'delegations' && (
        <DelegationsFirstSubstep
          creatorWizardState={{
            step: legacyCoords.step,
            subStep: legacyCoords.subStep,
            selectedDrivers,
          }}
          selectedDrivers={selectedDrivers}
          setSelectedDrivers={setSelectedDrivers}
          startTime={startTime}
          endTime={endTime}
          onNext={goToNext}
          onPrev={goToPrev}
        />
      )}

      {currentStageId === 'evidence.choose' && (
        <EvidenceFirstSubstep
          onGenerate={handleGenerateEvidence}
          onPrev={goToPrev}
          isLoading={isGeneratingEvidence}
        />
      )}
      {currentStageId === 'evidence.details' && isFullEvidence && companyId && (
        <EvidenceDetailsSubstep
          companyId={companyId}
          selectedDrivers={selectedDrivers}
          month={selectedMonthFromDashboard.format('YYYY-MM')}
          onNext={() => setCurrentStageId('summary.printouts')}
          onPrev={() => setCurrentStageId('evidence.choose')}
        />
      )}
      {currentStageId === 'summary.printouts' && (
        <SummaryFirstSubstep
          selectedReports={selectedReports}
          setSelectedReports={setSelectedReports}
          onNext={handleGeneratePrintouts}
          onPrev={goToPrev}
          selectedDriversCount={selectedDrivers.length}
          isGenerating={isGeneratingPrintouts || isGeneratingEvidence}
          generateToSeparateFiles={generateToSeparateFiles}
          setGenerateToSeparateFiles={setGenerateToSeparateFiles}
        />
      )}
      {currentStageId === 'summary.review' && companyId && (
        <SummarySecondSubstep
          companyId={companyId}
          selectedDrivers={selectedDrivers}
          month={selectedMonthFromDashboard.month() + 1}
          year={selectedMonthFromDashboard.year()}
          onPrev={goToPrev}
          onNext={() => navigate(dashboardHref)}
        />
      )}
    </CreatorLayout>
  )
}

export const CreatorPage: React.ComponentType = (props) => (
  <CreatorPageComponent {...(props as any)} addTask={addTask} />
)
